import { useContext, useEffect, useRef } from 'react'
import ModalContext from 'contexts/ModalContext'
import { useAppSelector } from 'hooks/reduxHooks'
import config from 'constants/config'
import {
  set as setLocalStorage,
  get as getLocalStorage,
} from 'lib/storage/isomorphicLocalStorage'
import { getImageUrl } from 'lib/image/imageUtils'
import AgentHubMaldivesEventModal, { MALDIVES_EVENT_IMAGE } from 'agentHub/components/AgentHubMaldivesEventModal'

const LOCAL_STORAGE_KEY_PREFIX = 'agentHub.maldives_event_modal_dismissed'

const getUserStorageKey = (userId: string) => `${LOCAL_STORAGE_KEY_PREFIX}.${userId}`

function preloadImage(imageId: string): Promise<void> {
  return new Promise((resolve) => {
    const img = new window.Image()
    img.onload = () => resolve()
    img.onerror = () => resolve()
    img.src = getImageUrl(imageId, { width: 600 })
  })
}

function agencyStateIsNewSouthWales(agencyState: string): boolean {
  return agencyState.toUpperCase().includes('NSW') || agencyState.toUpperCase().includes('NEW SOUTH WALES')
}

const EVENT_EXPIRY = new Date('2026-03-19T00:00:00+11:00')

function useAgentHubMaldivesEventModal() {
  const showModal = useContext(ModalContext)
  const account = useAppSelector(state => state.agentHub.agentHubAccount.account)
  const fetching = useAppSelector(state => state.agentHub.agentHubAccount.fetching)
  const userId = useAppSelector(state => state.auth.account.memberId)
  const agencyState = useAppSelector(state => state.agentHub.agentHubAccount.account?.agency?.state)
  const hasShown = useRef(false)

  useEffect(() => {
    if (!config.agentHub.isEnabled) return
    if (new Date() >= EVENT_EXPIRY) return
    if (fetching || account?.status !== 'onboarded') return
    if (!userId) return
    if (hasShown.current) return
    if (getLocalStorage(getUserStorageKey(userId))) return
    if (!agencyState || !agencyStateIsNewSouthWales(agencyState)) return

    hasShown.current = true
    preloadImage(MALDIVES_EVENT_IMAGE.id!).then(() => {
      showModal(<AgentHubMaldivesEventModal />).then(() => {
        if (config.APP_ENV === 'production') {
          setLocalStorage(getUserStorageKey(userId), true)
        }
      })
    })
  }, [showModal, account, fetching, userId, agencyState])
}

export default useAgentHubMaldivesEventModal
